import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, LinearProgress,
  Divider, List, ListItem, ListItemIcon, ListItemText, Button, Skeleton,
  Tooltip,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiServices';

function getRiskConfig(level) {
  switch (level) {
    case 'critical': return { color: '#DC2626', bg: '#FEF2F2', label: 'KRITIK', chipColor: 'error' };
    case 'high':     return { color: '#EA580C', bg: '#FFF7ED', label: 'I LARTË', chipColor: 'error' };
    case 'medium':   return { color: '#D97706', bg: '#FFFBEB', label: 'MESATAR', chipColor: 'warning' };
    default:         return { color: '#3E8FA3', bg: '#ECFDF5', label: 'I ULËT',  chipColor: 'success' };
  }
}

function ScoreBar({ label, value, max = 25, color }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <Box sx={{ mb: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.3 }}>
        <Typography variant="caption" fontWeight={600}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{value}/{max}</Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: 'rgba(0,0,0,0.08)',
          '& .MuiLinearProgress-bar': { bgcolor: pct >= 80 ? '#3E8FA3' : pct >= 60 ? '#D97706' : '#DC2626' },
        }}
      />
    </Box>
  );
}

export default function ComplianceScore({ compact = false }) {
  const [score, setScore]     = useState(null);
  const [prev, setPrev]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiService().query('getComplianceScores', '').then(r => {
      if (r?.success && r.data?.length) {
        setScore(r.data[0]);
        if (r.data.length > 1) setPrev(r.data[1]);
      }
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <Skeleton height={120} />
      </CardContent>
    </Card>
  );

  if (!score) return null;

  const { color, bg, label, chipColor } = getRiskConfig(score.RiskLevel);
  const totalPct = score.ScoreTotal || 0;
  const trend = prev ? score.ScoreTotal - prev.ScoreTotal : 0;

  if (compact) {
    return (
      <Card sx={{ borderRadius: 2, bgcolor: bg, border: `1px solid ${color}20` }}>
        <CardContent sx={{ p: '12px 16px !important' }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ textAlign: 'center', minWidth: 56 }}>
              <Typography variant="h4" fontWeight={900} sx={{ color, lineHeight: 1 }}>
                {score.ScoreTotal}
              </Typography>
              <Typography variant="caption" sx={{ color }}>/ 100</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                <Chip size="small" label={label} color={chipColor} />
                {trend !== 0 && (
                  <Chip
                    size="small"
                    icon={trend > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                    label={`${trend > 0 ? '+' : ''}${trend}`}
                    color={trend > 0 ? 'success' : 'error'}
                    variant="outlined"
                  />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Compliance Risk Score — {score.Issues?.length || 0} çështje aktive
              </Typography>
            </Box>
            <Button component={Link} to="/compliance" size="small" variant="outlined">
              Detaje
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Compliance Risk Score
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Përditësuar: {score.ScoredAt ? new Date(score.ScoredAt).toLocaleDateString('sq-AL') : '—'}
            </Typography>
          </Box>
          <Tooltip title="Si llogaritet: 100 - penalitete per çdo gap compliance">
            <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 2 }}>
          <Box
            sx={{
              width: 80, height: 80, borderRadius: '50%',
              background: `conic-gradient(${color} ${totalPct * 3.6}deg, #e5e7eb 0deg)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h5" fontWeight={900} sx={{ color, lineHeight: 1 }}>{score.ScoreTotal}</Typography>
              <Typography variant="caption" sx={{ color }}>/ 100</Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Chip label={label} color={chipColor} size="small" />
              {trend !== 0 && (
                <Chip
                  size="small"
                  icon={trend > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                  label={`${trend > 0 ? '+' : ''}${trend} vs. muajin e kaluar`}
                  color={trend > 0 ? 'success' : 'error'}
                  variant="outlined"
                />
              )}
            </Stack>
            <ScoreBar label="Kontratat"  value={score.ContractsScore || 0} max={25} />
            <ScoreBar label="Pagat"      value={score.PayrollScore || 0}   max={25} />
            <ScoreBar label="Pushimet"   value={score.LeavesScore || 0}    max={20} />
            <ScoreBar label="Prezenca"   value={score.PresenceScore || 0}  max={15} />
            <ScoreBar label="ATK"        value={score.ATKScore || 0}       max={15} />
          </Box>
        </Stack>

        {score.Issues?.length > 0 && (
          <>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="caption" fontWeight={700} color="error.main" sx={{ display: 'block', mb: 0.5 }}>
              Çështje aktive ({score.Issues.length}):
            </Typography>
            <List dense disablePadding>
              {score.Issues.map((issue, i) => (
                <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="caption">{issue}</Typography>}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </CardContent>
    </Card>
  );
}
